/**
 * Launch command - creates a new session, backed by a Cloudflare Sandbox instance
 *
 * Always connects via WebSocket terminal bridge (stdin/stdout in raw mode).
 */

import { checkSyncPayload } from '../sync/payload-size.js';
import chalk from 'chalk';
import * as readline from 'readline';
import { SANDBOX_WORKER_URL } from '@spacecode/shared/remote';
import type { ProxyMode } from '@spacecode/shared/remote';
import type { SandboxClient, SandboxInfo } from '@spacecode/sandbox-client';
import { createSignalHandler, registerSignalHandlers, removeSignalHandlers } from '../utils/signals.js';
import { logInfo, addBreadcrumb } from '../posthog.js';
import { resolveAuthToken, sandboxClientOptions } from '../config.js';
import { buildEnvVars } from '../utils/env.js';
import { isValidSandboxName } from '@spacecode/shared';
import { loadSessions, saveSession, markSessionDestroyed, isNameTaken } from '../sessions.js';

/**
 * Check if a GitHub repo exists and is not archived
 */
async function repoExists(owner: string, name: string): Promise<boolean> {
  // Validate inputs to prevent shell injection
  if (!/^[a-zA-Z0-9._-]+$/.test(owner) || !/^[a-zA-Z0-9._-]+$/.test(name)) {
    return false;
  }
  const { runOutput } = await import('../utils/exec.js');
  try {
    const json = await runOutput(`gh repo view ${owner}/${name} --json name,isArchived`);
    const repo = JSON.parse(json);
    return !repo.isArchived;
  } catch {
    return false;
  }
}

/**
 * Expand a short repo name to full GitHub URL
 * Searches user's repos and org repos if just a name is provided
 * Examples:
 *   "myrepo" -> "github.com/username/myrepo" or "github.com/org/myrepo"
 *   "owner/repo" -> "github.com/owner/repo"
 *   "github.com/owner/repo" -> unchanged
 *   "https://github.com/owner/repo" -> unchanged
 */
async function expandRepoName(repo: string): Promise<string> {
  const { runOutput } = await import('../utils/exec.js');

  // Already a full URL
  if (repo.includes('://')) {
    return repo;
  }

  // Has github.com but no protocol prefix
  if (repo.includes('github.com')) {
    return `https://${repo}`;
  }

  // Has owner/repo format
  if (repo.includes('/')) {
    // Validate format to prevent shell injection
    if (!/^[a-zA-Z0-9._-]+\/[a-zA-Z0-9._-]+$/.test(repo)) {
      throw new Error(`Invalid repo format: "${repo}"`);
    }
    return `https://github.com/${repo}`;
  }

  // Validate repo name to prevent shell injection
  if (!/^[a-zA-Z0-9._-]+$/.test(repo)) {
    throw new Error(`Invalid repo name: "${repo}"`);
  }

  // Just a repo name - search for it
  try {
    // Get username
    const userJson = await runOutput('gh api user');
    const user = JSON.parse(userJson);
    const username = user.login;

    if (username) {
      // First check if repo exists under user's account
      if (await repoExists(username, repo)) {
        return `https://github.com/${username}/${repo}`;
      }

      // Get list of orgs user has access to
      const orgsJson = await runOutput('gh api user/orgs --jq ".[].login"');
      const orgs = orgsJson.trim().split('\n').filter(Boolean);

      // Check each org for the repo
      for (const org of orgs) {
        if (await repoExists(org, repo)) {
          return `https://github.com/${org}/${repo}`;
        }
      }

      // Not found anywhere, default to user's namespace
      console.log(chalk.yellow(`Warning: Repo '${repo}' not found. Defaulting to ${username}/${repo}`));
      return `https://github.com/${username}/${repo}`;
    }
  } catch (e) {
    console.warn(chalk.dim(`  Could not expand repo name via GitHub CLI — using as-is: ${e instanceof Error ? e.message : String(e)}`));
  }

  return repo;
}

export interface LaunchOptions {
  repo?: string;          // GitHub repository URL to clone into workspace
  newProject?: boolean;   // Start a new project workflow
  newProjectName?: string; // Pre-specified project name (skips prompt if provided)
  destroy?: boolean;      // Destroy session on exit (one-shot mode)
  idleTimeout?: number;   // Auto-shutdown timeout in minutes (default: 15)
  proxyMode?: ProxyMode;
  name?: string;          // Named session for easy reconnection
  ownKey?: boolean;       // BYOK: skip proxy, let user auth in sandbox
  apiKey?: string;        // BYOK: use a specific Anthropic API key
}

/**
 * Prompt user for input
 */
async function prompt(question: string): Promise<string> {
  const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout,
  });

  return new Promise((resolve) => {
    rl.question(question, (answer) => {
      rl.close();
      resolve(answer.trim());
    });
  });
}

/**
 * Prompt user for yes/no
 */
async function promptYesNo(question: string): Promise<boolean> {
  const answer = await prompt(`${question} (y/n): `);
  return answer.toLowerCase() === 'y' || answer.toLowerCase() === 'yes';
}

export async function launch(options: LaunchOptions): Promise<void> {
  // Track cleanup state for signal handlers (scoped to this invocation)
  let cleanupInProgress = false;
  let cleanupDone = false;
  let activeSessionId: string | null = null;
  let activeClient: SandboxClient | null = null;

  /**
   * Cleanup function that destroys the session
   * Can be called from signal handlers or normal exit
   */
  async function cleanupSession(signal?: string): Promise<void> {
    if (cleanupDone || cleanupInProgress || !activeSessionId || !activeClient) {
      return;
    }

    cleanupInProgress = true;

    try {
      await activeClient.destroySandbox(activeSessionId);
      await markSessionDestroyed(activeSessionId).catch((err) => {
        console.warn(chalk.dim(`Warning: Could not update local session registry: ${err instanceof Error ? err.message : String(err)}`));
      });
      console.log(chalk.green('Session destroyed.'));
      cleanupDone = true;
    } catch (error) {
      console.log(chalk.yellow('Warning: Failed to destroy session.'));
      console.log(chalk.dim(`Session ID: ${activeSessionId}`));
      console.log(chalk.dim(`Error: ${error instanceof Error ? error.message : String(error)}`));
      console.log(chalk.yellow(`Run \`spacecode destroy ${activeSessionId}\` to clean up manually.`));
    } finally {
      cleanupInProgress = false;
    }
  }

  // Track launch in Sentry
  addBreadcrumb('launch', 'cli', 'info');
  logInfo('spacecode launch started', {
    hasRepo: !!options.repo,
    isNewProject: options.newProject,
    destroy: options.destroy,
  });

  console.log(chalk.bold('\nspacecode launch\n'));

  // ── Interactive prompts (before any async work) ──────────────────────

  let repoUrl: string | undefined;

  if (!options.repo && !options.newProject) {
    const startNew = await promptYesNo(chalk.bold('Start a new project?'));
    if (startNew) {
      options.newProject = true;
    }
  }

  let newProjectName: string | undefined;

  if (options.newProject) {
    newProjectName = options.newProjectName;

    if (!newProjectName) {
      console.log('');
      console.log(chalk.cyan('New Project Setup'));
      newProjectName = await prompt(chalk.bold('Project name: '));
    }

    if (!newProjectName) {
      console.error(chalk.red('Error: Project name is required.'));
      process.exit(1);
    }

    // Validate project name (alphanumeric, hyphens, underscores)
    if (!/^[a-zA-Z0-9_-]+$/.test(newProjectName)) {
      console.error(
        chalk.red('Error: Project name can only contain letters, numbers, hyphens, and underscores.')
      );
      process.exit(1);
    }

    console.log(chalk.dim(`Will create GitHub repo: ${newProjectName}`));
    console.log(
      chalk.dim('After connecting, paste your PRD and the AI agent will guide you through project setup.')
    );
    console.log('');
  }

  // ── Parallel setup: auth, config, repo expansion, name check ────────
  // All independent — run concurrently to minimize wall-clock time.

  console.log(chalk.dim('Setting up...'));

  const syncImport = import('../sync/config.js');
  const clientImport = import('@spacecode/sandbox-client');

  const [token, { getGitHubToken }, { createSandboxClient }] = await Promise.all([
    resolveAuthToken(),
    syncImport,
    clientImport,
  ]);

  // Run repo expansion, config gathering, and name check in parallel
  const repoPromise = options.repo ? expandRepoName(options.repo) : Promise.resolve(undefined);
  const ghTokenPromise = getGitHubToken();
  const gitConfigPromise = (async () => {
    const { runOutput } = await import('../utils/exec.js');
    const [name, email] = await Promise.all([
      runOutput('git config --global user.name').catch(() => ''),
      runOutput('git config --global user.email').catch(() => ''),
    ]);
    return { name: name.trim(), email: email.trim() };
  })();
  const nameCheckPromise = options.name ? (async () => {
    if (!isValidSandboxName(options.name!)) {
      console.error(chalk.red(`\nInvalid session name "${options.name}".`));
      console.log(chalk.dim('Names must be 1-64 characters starting with alphanumeric, then alphanumeric, dots, hyphens, or underscores.'));
      process.exit(1);
    }
    const sessions = await loadSessions();
    const existing = isNameTaken(sessions, options.name!);
    if (existing) {
      console.log(chalk.yellow(`\nSession "${options.name}" already exists (${existing.id}, ${existing.status}).`));
      if (existing.status === 'running') {
        console.log(chalk.dim(`Run: spacecode attach ${options.name}`));
      }
      process.exit(1);
    }
  })() : Promise.resolve();

  const [resolvedRepo, ghToken, gitConfig] = await Promise.all([
    repoPromise,
    ghTokenPromise,
    gitConfigPromise,
    nameCheckPromise,
  ]);

  // Startup files written into the sandbox (the .bashrc pair below).
  const files: Array<{ path: string; content: string }> = [];

  repoUrl = resolvedRepo;
  if (repoUrl) console.log(chalk.dim(`Repo: ${repoUrl}`));

  const envVars = buildEnvVars({
    ghToken,
    proxyMode: options.proxyMode,
    newProjectName,
    gitUserName: gitConfig.name,
    gitUserEmail: gitConfig.email,
    skipProxy: options.ownKey,
    customApiKey: options.apiKey,
  });

  // Signals the sandbox-worker that this is a CLI terminal session (isCLIMode).
  envVars.SPACECODE_AGENT_STARTED = '1';

  // Setup (env, cd, clear) runs inside .bashrc during shell init, so nothing is typed into the PTY.

  const repoName = repoUrl
    ? (repoUrl.split('/').pop()?.replace(/\.git$/, '') ?? 'workspace')
    : '';
  if (repoName && !/^[a-zA-Z0-9._-]+$/.test(repoName)) {
    console.error(chalk.red(`Error: Unsafe repository name derived from URL: "${repoName}"`));
    process.exit(1);
  }
  const workDir = repoUrl ? `/workspace/${repoName}` : '/workspace';

  // The sandbox container starts as root, but the workspace and login shell
  // belong to the non-root "user" account. Copy env and git config over and switch.
  files.push({
    path: '/root/.bashrc',
    content: [
      '# spacecode: switch to non-root user',
      'if [ -z "$_CR_INIT" ]; then',
      '  export _CR_INIT=1',
      '  cp /root/.env /home/user/.env 2>/dev/null',
      '  cp /root/.gitconfig /home/user/.gitconfig 2>/dev/null',
      '  chown -R user:user /home/user/ /workspace/ 2>/dev/null',
      '  [ ! -f /home/user/.env ] && echo "WARNING: .env not copied - env vars may be missing. Re-run spacecode launch." >&2',
      '  exec su - user',
      'fi',
    ].join('\n') + '\n',
  });
  // Ensure login shell sources .bashrc
  files.push({
    path: '/home/user/.bash_profile',
    content: '[ -f ~/.bashrc ] && . ~/.bashrc\n',
  });
  // Login shell: source env and open the repo directory. No agent is launched.
  const userBashrc = [
    '# spacecode init (guarded to run once, not in subshells)',
    'if [ -z "$_SPACECODE_INIT" ]; then',
    '  export _SPACECODE_INIT=1',
    '  source ~/.env 2>/dev/null',
    `  cd ${workDir} 2>/dev/null`,
    '  clear',
    'fi',
  ].join('\n') + '\n';
  files.push({
    path: '/home/user/.bashrc',
    content: userBashrc,
  });

  // ── Create session ───────────────────────────────────────────────────

  try {
    checkSyncPayload(files);
  } catch (error) {
    console.error(chalk.red(`\n${error instanceof Error ? error.message : String(error)}`));
    process.exit(1);
  }

  console.log(chalk.dim(`Creating session... (${files.length} config files)`));
  const client = createSandboxClient(SANDBOX_WORKER_URL, token, sandboxClientOptions(token));

  let sessionId: string;
  let sandboxInfo: SandboxInfo;

  try {
    sandboxInfo = await client.createSandbox({
      repo: repoUrl,
      files,
      envVars,
      idleTimeoutMinutes: options.idleTimeout,
      name: options.name,
    });

    sessionId = sandboxInfo.id;
    activeSessionId = sessionId;
    activeClient = client;

    // Save to local session registry
    await saveSession({
      id: sessionId,
      name: options.name,
      repo: repoUrl,
      status: 'running',
      createdAt: new Date().toISOString(),
      idleTimeoutMinutes: options.idleTimeout,
    });

    const displayName = options.name ? `${sessionId} (${options.name})` : sessionId;
    console.log(chalk.green(`Session created: ${displayName}`));

    if (sandboxInfo.warnings && sandboxInfo.warnings.length > 0) {
      for (const w of sandboxInfo.warnings) {
        console.log(chalk.yellow(`  Warning: ${w}`));
      }
    }
  } catch (error) {
    console.error(chalk.red(`\nError creating session: ${error instanceof Error ? error.message : String(error)}`));
    process.exit(1);
  }

  // ── Register signal handlers for cleanup ─────────────────────────────

  const handler = createSignalHandler(cleanupSession, true);
  registerSignalHandlers(handler);

  // ── Connect via WebSocket terminal ──────────────────────────────────

  const wsUrl = sandboxInfo.wsUrl ?? client.getWsUrl(sessionId);
  console.log(chalk.dim('Connecting...\n'));

  try {
    const { connectTerminal } = await import('../utils/ws-terminal.js');
    const exitCode = await connectTerminal(wsUrl, sessionId);

    console.log(chalk.dim('\nSession ended.'));

    removeSignalHandlers();
    while (cleanupInProgress) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    if (options.destroy) {
      if (!cleanupDone) {
        await cleanupSession();
      }
    } else {
      console.log(chalk.dim(`Session still running (no web UI yet; ADR-15).`));
      console.log(chalk.dim(`Or reconnect: spacecode attach ${options.name ?? sessionId}`));
      console.log(chalk.dim(`Destroy: spacecode destroy ${options.name ?? sessionId}`));
    }

    process.exit(exitCode);
  } catch (error) {
    console.error(chalk.red('\nError: WebSocket connection failed.'));
    console.error(chalk.dim(String(error)));

    removeSignalHandlers();
    while (cleanupInProgress) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    if (options.destroy && !cleanupDone) {
      await cleanupSession();
    } else if (!options.destroy) {
      console.log(chalk.dim(`\nSession ${sessionId} is still running.`));
      console.log(chalk.dim(`Destroy: spacecode destroy ${options.name ?? sessionId}`));
    }

    process.exit(1);
  }
}
